import * as React from "react";
import {useEffect, useState} from "react";
import {Button, Input, Tooltip} from "@mui/material";
import {CloudUploadOutlined} from "@mui/icons-material";
import * as mime from "mime";
import axios from "axios";
import {DRIVE_TYPES, UPLOAD_STATUS} from "../../config/appConst";
import useDrive from "../../hooks/useDrive";
import useNotification from "../../hooks/useNotification";
import FileService from "../../services/FileService";
import DriveService from "../../services/DriveService";
import FileExistConformation from "../FilesAndFoldersTbl/FileExistConformation";
import ProgressSnack from "./ProgressSnack";

export default function FileUpload({droppedFile, hideUploadButton}) {
  const {changeCurrentDrive, changeUploadStatus, CurrentDrive} = useDrive();
  const {addNotification} = useNotification();

  const [openExistConfirmation, setOpenExistConfirmation] = useState(false);
  const [file, setFile]               = useState(new Blob());
  const [fileDetailObject, setFileDetailObject] = useState({});
  const [existenceRes, setExistenceRes]         = useState({});
  const [progress, setProgress]       = useState(0);

  useEffect(() => { if (droppedFile) { setProgress(0); setFile(droppedFile); } }, [droppedFile]);

  useEffect(() => {
    if (file?.size > 0) {
      setProgress(0);
      const parent = CurrentDrive.parent === "" ? "/" : CurrentDrive.parent;
      fileServeValidator(file.name, file.size, parent).then(res => { if (res) setExistenceRes(res); });
    }
  }, [file]);

  useEffect(() => {
    if (existenceRes) {
      if (existenceRes.exists === 0) doUpload().then(() => { setFile(new Blob()); setExistenceRes({}); });
      else setOpenExistConfirmation(existenceRes.exists === 1);
    }
  }, [existenceRes]);

  const handleFileSelect = e => { const f = e.target.files[0]; if (f) setFile(f); };

  const fileServeValidator = async (file_name, size, folder) => {
    try {
      const {driveType} = CurrentDrive;
      return await FileService.checkFile({file_name, size, folder}, driveType);
    } catch (e) { console.error(e); }
  };

  const doUpload = async (saveAsNew = false) => {
    changeUploadStatus(UPLOAD_STATUS.UPLOADING);
    let {parent, driveType} = CurrentDrive;
    parent = parent === "" ? "/" : parent;
    const file_name = saveAsNew ? existenceRes.file_name : file.name;
    const size = file.size;
    setFileDetailObject({file_name, size, folder: parent});
    const bucketUrl   = await DriveService.getS3BucketUrl({file_name, size, folder: parent}, driveType);
    const contentType = mime.getType(file_name) || "application/octet-stream";
    const reader      = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      await axios({ method: "PUT", url: bucketUrl, headers: {"Content-Type": contentType},
        onUploadProgress: p => setProgress((p.loaded / p.total) * 100),
        data: file,
      });
      changeUploadStatus(UPLOAD_STATUS.COMPLETING);
      const drive = await DriveService.getFileTree(parent, driveType);
      changeCurrentDrive({
        parent,
        drive:       (!driveType || driveType.length === 0) ? drive : {},
        sharedDrive: driveType === DRIVE_TYPES.SHARED ? drive : {},
        driveType,
      });
      changeUploadStatus(UPLOAD_STATUS.DONE);
      const el = document.getElementById("sf-file-upload-input");
      if (el) el.value = null;
      addNotification({type: "success", message: "File uploaded successfully"});
    };
  };

  return (
    <>
      {!hideUploadButton && (
        <>
          <Tooltip title="Upload files" arrow>
            <label htmlFor="sf-file-upload-input" style={{ cursor: "pointer" }}>
              <Input id="sf-file-upload-input" type="file" accept="*/*"
                style={{ display: "none" }} onChange={handleFileSelect} />
              <Button
                component="span"
                variant="contained"
                size="small"
                startIcon={<CloudUploadOutlined sx={{ fontSize: 16 }} />}
                sx={{
                  height: 36, fontSize: "13px", fontWeight: 600,
                  textTransform: "none", borderRadius: 2, px: 2,
                  bgcolor: "#e85878", color: "#fff",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#c43058", boxShadow: "none" },
                }}
              >
                Upload
              </Button>
            </label>
          </Tooltip>
          <FileExistConformation open={openExistConfirmation}
            setOpen={setOpenExistConfirmation} doUpload={doUpload} />
        </>
      )}
      {hideUploadButton && (
        <FileExistConformation open={openExistConfirmation}
          setOpen={setOpenExistConfirmation} doUpload={doUpload} />
      )}
      <ProgressSnack value={progress} fileDetail={fileDetailObject} />
    </>
  );
}
